/**
 * FhirOutputPreview - collapsible FHIR JSON preview (Sprint 3.4 REDO).
 *
 * Builds a deterministic `QuestionnaireResponse` example from the
 * current selection + schedule assignment using the existing PROM
 * mapper at `services/fhir-mappers/prom-to-questionnaire-response.ts`.
 * Re-renders on selection change. Same selection always produces the
 * same JSON so HC1 reproducibility holds.
 *
 * The preview shows the FIRST selected instrument's QuestionnaireResponse
 * with one synthetic baseline answer per item-group. The full publish
 * payload is composed elsewhere (the publish CTA emits a PlanDefinition).
 *
 * HC1 reproducibility: mapper output is byte-deterministic for a given
 *   selection. No clocks, no randomness.
 * HC2 token-only.
 * HC5 zero em / en dashes.
 */
import { useMemo, useState } from 'react';
import { promToFhir } from '../../services/fhir-mappers';
import type { PromSubmission } from '../../services/fhir-mappers';
import {
  ADMIN_SCHEDULE_STEPS,
  type PromInstrumentCode,
  type ScheduleAssignments,
} from './types';
import { PROM_INSTRUMENTS } from './types';

export interface FhirOutputPreviewProps {
  selectedInstruments: PromInstrumentCode[];
  assignments: ScheduleAssignments;
  /** Tenant id used when building the preview. */
  tenantId: string;
  /** Stable timestamp for deterministic preview. */
  authored?: string;
}

const DEFAULT_AUTHORED = '2026-05-07T00:00:00.000Z';

/** LOINC codes used as placeholders for the preview score. */
const LOINC_BY_CODE: Record<PromInstrumentCode, string> = {
  MSQ: '82667-7',
  'PHQ-9': '44261-6',
  'SF-12': '54809-7',
  GSRS: '79006-3',
  IWQOL: '93947-4',
  DQOLY: '76507-3',
  'GAD-7': '70274-6',
  'EQ-5D': '89544-3',
  KOOS: '93948-2',
};

/** Build a deterministic submission for a given instrument. */
export function buildPreviewSubmission(
  code: PromInstrumentCode,
  tenantId: string,
  authored: string,
  steps: readonly { id: string; label: string }[],
): PromSubmission {
  const descriptor = PROM_INSTRUMENTS.find((i) => i.code === code);
  if (!descriptor) {
    throw new Error(`Unknown PROM instrument code "${code}"`);
  }
  return {
    submissionId: `preview-${code.toLowerCase()}-${steps[0]?.id ?? 'baseline'}`,
    questionnaireUrl: `https://prom.scrips.com/Questionnaire/${code.toLowerCase()}`,
    patientReference: 'Patient/synthetic-preview',
    practitionerReference: 'Practitioner/synthetic-preview',
    tenantId,
    authored,
    items: [
      {
        linkId: `${code.toLowerCase()}-q1`,
        text: `${descriptor.name} item 1`,
        value: 0,
      },
    ],
    score: {
      loincCode: LOINC_BY_CODE[code],
      display: `${descriptor.name} total score`,
      value: 0,
      unit: 'score',
    },
  };
}

export function FhirOutputPreview({
  selectedInstruments,
  assignments,
  tenantId,
  authored = DEFAULT_AUTHORED,
}: FhirOutputPreviewProps) {
  const [expanded, setExpanded] = useState(false);

  const previewJson = useMemo(() => {
    if (selectedInstruments.length === 0) {
      return null;
    }
    const firstCode = selectedInstruments[0];
    const submission = buildPreviewSubmission(
      firstCode,
      tenantId,
      authored,
      ADMIN_SCHEDULE_STEPS,
    );
    const { questionnaireResponse } = promToFhir(submission);
    // Decorate with the schedule-step extension list so a clinician
    // reading the preview can confirm which step the response targets.
    const stepIds = ADMIN_SCHEDULE_STEPS.filter((step) =>
      assignments[step.id].includes(firstCode),
    ).map((step) => step.id);
    const decorated = {
      ...questionnaireResponse,
      _scheduleSteps: stepIds,
    };
    return JSON.stringify(decorated, null, 2);
  }, [selectedInstruments, assignments, tenantId, authored]);

  const previewId = 'fhir-output-preview-body';

  return (
    <section
      data-testid="fhir-output-preview"
      aria-labelledby="fhir-output-preview-title"
      className="rounded-signal-lg border border-surface-border bg-surface-base p-gutter-lg shadow-signal-card"
    >
      <header className="flex flex-wrap items-baseline justify-between gap-gutter-md">
        <h3
          id="fhir-output-preview-title"
          className="text-signal-lg font-semibold text-text-primary"
        >
          Output Preview
        </h3>
        <button
          type="button"
          data-testid="fhir-output-preview-toggle"
          aria-expanded={expanded}
          aria-controls={previewId}
          onClick={() => setExpanded((prev) => !prev)}
          className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-md py-1 text-signal-xs font-semibold text-text-primary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
        >
          {expanded ? 'Collapse' : 'Expand'}
        </button>
      </header>
      <p className="mt-gutter-sm text-signal-sm text-text-secondary">
        FHIR `QuestionnaireResponse` for the first selected instrument.
        Re-renders on selection change.
      </p>
      {previewJson === null ? (
        <p
          data-testid="fhir-output-preview-empty"
          className="mt-gutter-md rounded-signal-md border border-dashed border-surface-border bg-surface-muted p-gutter-md text-signal-sm text-text-tertiary"
        >
          Select at least one instrument to preview the FHIR output.
        </p>
      ) : (
        <pre
          id={previewId}
          data-testid="fhir-output-preview-json"
          hidden={!expanded}
          className="mt-gutter-md max-h-72 overflow-auto rounded-signal-md bg-surface-muted p-gutter-md text-signal-xs leading-relaxed text-text-primary"
        >
          {previewJson}
        </pre>
      )}
    </section>
  );
}
